import { useMemo, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import PropTypes from 'prop-types';
import { SchemaRenderer } from '@/schemas/components/SchemaRenderer';
import { sensorCardAdapter, updateSensorStateFromSSE } from '@/schemas/adapters';
import useGetLatestUserEvents from '@/Components/Hooks/useGetLatestUserEvents';
import useEventsAggregateByTimeRange from '@/Components/Hooks/useEventsAggregateByTimeRange';
import useGetUserEvents from '@/Components/Hooks/useGetUserEvents';
import { sensorsSchema } from '@/schemas/sensors';
import {
  EVENT_PROPERTIES,
  SENSORS,
  TIME_RANGE,
  DEVICE_CONNECTION_STATUS,
} from '@/constants/device';
import { SSEEventContext } from '@/Components/SmartHome/SSEEventContext';
import { getSensorIcon } from '@/Components/Common/deviceUtils';
import { getLatestUpdateEventTypes } from '@/utilities/helpers/commonHelper';
import { get, isEmpty, isNil, lowerCase } from 'lodash';
import moment from 'moment-timezone';
import styled from 'styled-components';

const Wrapper = styled.div`
  cursor: pointer;
  &.outdoor_temperature_humidity_sensor {
    cursor: default;
  }
  &.grey-card {
    .title-blue,
    .model-name,
    path,
    .content-value,
    .content-category,
    .card-today-title,
    circle,
    .sensor-state-title {
      color: #808080;
      fill: #808080 !important;
    }
  }
  &.cooktop_sensor,
  &.occupancy_sensor,
  &.door_window_sensor,
  &.smart_plug,
  &.toilet_flush_sensor,
  &.radar_sensor {
    .current-state-container {
      padding-left: 8px;
    }
  }
`;

export const SensorCard = ({
  sensor,
  useruuid,
  timezone = '',
  loginUserTimeformat,
  loginUserLocale = '',
  tempUnit = '',
  outdoorData,
  setShowModal,
  setSelectedSensorDetails,
  setSensorInfoIcon,
}) => {
  const utils = {};
  const { deviceuuid, devicetype, connectionless = false } = sensor;
  const trans = useSelector(translator);

  const [formattedData, setFormattedData] = useState({});

  const isGreyCard = useMemo(() => {
    const isEnvironmentalCardStale =
      !connectionless &&
      formattedData?.date !== trans(TIME_RANGE.TODAY) &&
      (devicetype === SENSORS.SMART_HOME.ENVIRONMENTAL_SENSOR ||
        devicetype === SENSORS.SMART_HOME.TEMPERATURE_HUMIDITY_SENSOR);

    const isSmartPlugDisconnected =
      devicetype === SENSORS.SMART_HOME.SMART_PLUG &&
      formattedData?.sensorStatus?.includes(lowerCase(DEVICE_CONNECTION_STATUS.DISCONNECTED));

    return isEnvironmentalCardStale || isSmartPlugDisconnected;
  }, [connectionless, formattedData?.date, formattedData?.sensorStatus, devicetype, trans]);

  const schema = sensorsSchema[devicetype];

  const { sseEvent } = useContext(SSEEventContext);

  const sensorModelName = get(sensor, 'modelname');
  const sensorModelNumber = get(sensor, 'modelnumber');
  const envEventTypes = useMemo(
    () =>
      getLatestUpdateEventTypes({
        devicetype,
        modelname: sensorModelName,
        modelnumber: sensorModelNumber,
      }),
    [devicetype, sensorModelName, sensorModelNumber]
  );

  const { data: sensorLatestEvents } = useGetLatestUserEvents(
    {
      useruuid,
      deviceuuid,
      envEventTypes,
    },
    {
      refetchOnMount: 'always',
      staleTime: 0,
    }
  );

  const timezoneName = useMemo(() => timezone?.split(' ')[0] || moment.tz.guess(), [timezone]);

  const { startDatetime, endDatetime } = useMemo(() => {
    if (!timezoneName) {
      return { startDatetime: null, endDatetime: null };
    }
    const nowInZone = moment.tz(timezoneName);
    const startOfRange = nowInZone
      .clone()
      .subtract(2, 'day')
      .startOf('day')
      .utc()
      .format('YYYY-MM-DDTHH:mm:ss[Z]');
    const endOfRange = nowInZone.clone().endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
    return { startDatetime: startOfRange, endDatetime: endOfRange };
  }, [timezoneName]);

  const aggregateFields = useMemo(() => {
    if (devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR) {
      return [{ name: 'active', values: ['true'] }];
    }
    if (devicetype === SENSORS.SMART_HOME.RADAR_SENSOR) {
      return [{ name: 'presence_type', values: ['fall_detected'] }];
    }
    return undefined;
  }, [devicetype]);

  const canFetchSmartHomeAggregation =
    (devicetype === SENSORS.SMART_HOME.TOILET_FLUSH_SENSOR ||
      devicetype === SENSORS.SMART_HOME.RADAR_SENSOR) &&
    !!useruuid &&
    !!deviceuuid &&
    !!startDatetime &&
    !!endDatetime &&
    Array.isArray(envEventTypes) &&
    envEventTypes.length > 0;

  const { data: aggregatedEvents } = useEventsAggregateByTimeRange(
    {
      useruuid,
      deviceuuid,
      envEventTypes,
      startTime: startDatetime,
      endTime: endDatetime,
      aggregateFields,
    },
    {
      enabled: canFetchSmartHomeAggregation,
    }
  );

  const todayStartDatetime = useMemo(() => {
    if (!timezoneName) return null;
    return moment.tz(timezoneName).startOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  }, [timezoneName]);

  const todayEndDatetime = useMemo(() => {
    if (!timezoneName) return null;
    return moment.tz(timezoneName).endOf('day').utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
  }, [timezoneName]);

  const { data: radarTodayEvents } = useGetUserEvents({
    useruuid,
    deviceuuid,
    startDate: todayStartDatetime,
    endDate: todayEndDatetime,
    eventTypes: envEventTypes,
    open: devicetype === SENSORS.SMART_HOME.RADAR_SENSOR,
  });

  const { data: cooktopTodayEvents } = useGetUserEvents({
    useruuid,
    deviceuuid,
    startDate: todayStartDatetime,
    endDate: todayEndDatetime,
    eventTypes: envEventTypes,
    open: devicetype === SENSORS.SMART_HOME.COOKTOP_SENSOR,
  });

  useEffect(() => {
    if (outdoorData && devicetype === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR) {
      const data = sensorCardAdapter({
        sensor,
        sensorLatestEvents: [sensor],
        timezone,
        loginUserTimeformat,
        loginUserLocale,
        trans,
        tempUnit,
      });
      setFormattedData(data);
    }
    if (!isNil(sensorLatestEvents)) {
      const data = sensorCardAdapter({
        sensor,
        sensorLatestEvents: sensorLatestEvents || [],
        timezone,
        loginUserTimeformat,
        loginUserLocale,
        trans,
        tempUnit,
        aggregatedEvents,
        envEventTypes,
        radarTodayEvents,
        cooktopTodayEvents,
      });
      setFormattedData(data);
    }
  }, [
    sensorLatestEvents,
    sensor,
    timezone,
    loginUserTimeformat,
    loginUserLocale,
    trans,
    tempUnit,
    outdoorData,
    devicetype,
    aggregatedEvents,
    envEventTypes,
    radarTodayEvents,
    cooktopTodayEvents,
  ]);

  useEffect(() => {
    if (get(sseEvent, EVENT_PROPERTIES.ENTITY_UUID, '') !== deviceuuid) {
      return;
    }

    setFormattedData((prev) => {
      const updatedData = updateSensorStateFromSSE(
        prev,
        sseEvent,
        sensor,
        trans,
        tempUnit,
        timezone,
        loginUserTimeformat
      );

      return { ...prev, ...updatedData };
    });
  }, [sseEvent, deviceuuid, sensor, trans, tempUnit, timezone, loginUserTimeformat]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (devicetype === SENSORS.SMART_HOME.OUTDOOR_TEMPERATURE_HUMIDITY_SENSOR) {
      return;
    }
    setShowModal(true);
    setSelectedSensorDetails(sensor);
    setSensorInfoIcon(getSensorIcon(formattedData));
  };

  return (
    <Wrapper
      key={deviceuuid}
      className={`card-container ${devicetype} ${isGreyCard ? 'grey-card' : ''}`}
      onClick={handleClick}
    >
      {!isEmpty(formattedData) && (
        <SchemaRenderer key={deviceuuid} schema={schema} data={formattedData} scope={utils} />
      )}
    </Wrapper>
  );
};

SensorCard.propTypes = {
  sensor: PropTypes.shape({
    deviceuuid: PropTypes.string.isRequired,
    devicetype: PropTypes.string.isRequired,
    connectionless: PropTypes.bool,
  }).isRequired,
  useruuid: PropTypes.string.isRequired,
  loginUserTimeformat: PropTypes.shape({ is24hoursformat: PropTypes.bool }),
  timezone: PropTypes.string.isRequired,
  loginUserLocale: PropTypes.string.isRequired,
  tempUnit: PropTypes.string.isRequired,
  outdoorData: PropTypes.object,
  setShowModal: PropTypes.func.isRequired,
  setSelectedSensorDetails: PropTypes.func.isRequired,
  setSensorInfoIcon: PropTypes.func.isRequired,
};

export default SensorCard;
